import { supabase } from '../lib/supabaseClient';
import { Application } from '../types';

// Application Notes types and functions

export interface ApplicationNote {
  id: string;
  application_id: string;
  entity_id: string;
  author_id: string | null;
  author_name: string;
  author_role: string | null;
  content: string;
  note_type: 'comment' | 'status_change' | 'system';
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export const getApplicationNotes = async (applicationId: string): Promise<ApplicationNote[]> => {
  try {
    const { data, error } = await supabase
      .from('application_notes')
      .select('*')
      .eq('application_id', applicationId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching application notes:', error);
      return [];
    }

    return data as ApplicationNote[];
  } catch (error) {
    console.error('Error in getApplicationNotes:', error);
    return [];
  }
};

export const addApplicationNote = async (params: {
  applicationId: string;
  entityId: string;
  authorId: string;
  authorName: string;
  authorRole: string;
  content: string;
  noteType?: 'comment' | 'status_change' | 'system';
  metadata?: Record<string, any>;
}): Promise<ApplicationNote | null> => {
  try {
    const { data, error } = await supabase
      .from('application_notes')
      .insert({
        application_id: params.applicationId,
        entity_id: params.entityId,
        author_id: params.authorId,
        author_name: params.authorName,
        author_role: params.authorRole,
        content: params.content,
        note_type: params.noteType || 'comment',
        metadata: params.metadata || {},
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding application note:', error);
      return null;
    }

    return data as ApplicationNote;
  } catch (error) {
    console.error('Error in addApplicationNote:', error);
    return null;
  }
};

export const addStatusChangeNote = async (
  applicationId: string,
  entityId: string,
  authorId: string,
  authorName: string,
  authorRole: string,
  oldStatus: string,
  newStatus: string
): Promise<ApplicationNote | null> => {
  const statusLabels: Record<string, string> = {
    pending: 'New',
    reviewing: 'Under Review',
    shortlisted: 'Shortlisted',
    rejected: 'Rejected',
    hired: 'Hired',
  };

  const content = `Status changed from ${statusLabels[oldStatus] || oldStatus} to ${statusLabels[newStatus] || newStatus}`;

  return addApplicationNote({
    applicationId,
    entityId,
    authorId,
    authorName,
    authorRole,
    content,
    noteType: 'status_change',
    metadata: { oldStatus, newStatus },
  });
};

/**
 * Application Service
 * Handles all operations related to job applications
 */

export interface ApplicationFilters {
  entityId?: string;
  jobId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

// Submit a new application
export const submitApplication = async (
  applicationData: {
    jobId: string;
    entityId: string;
    applicantName: string;
    applicantEmail: string;
    applicantPhone?: string;
    resumeUrl: string;
    coverLetter?: string;
  }
): Promise<Application | null> => {
  try {
    const { data, error } = await supabase
      .from('applications')
      .insert([
        {
          job_id: applicationData.jobId,
          entity_id: applicationData.entityId,
          applicant_name: applicationData.applicantName,
          applicant_email: applicationData.applicantEmail,
          applicant_phone: applicationData.applicantPhone,
          resume_url: applicationData.resumeUrl,
          cover_letter: applicationData.coverLetter,
          status: 'pending',
        },
      ])
      .select()
      .single();

    if (error) {
      console.error('Error submitting application:', error);
      return null;
    }

    return {
      ...data,
      submittedAt: new Date(data.submitted_at),
    } as Application;
  } catch (error) {
    console.error('Error in submitApplication:', error);
    return null;
  }
};

// Get applications for a job
export const getApplicationsByJob = async (
  jobId: string,
  filters: ApplicationFilters = {}
): Promise<{ applications: Application[]; total: number }> => {
  try {
    let query = supabase
      .from('applications')
      .select('*', { count: 'exact' })
      .eq('job_id', jobId)
      .is('deleted_at', null);

    // Apply status filter
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    // Apply sorting
    query = query.order('submitted_at', { ascending: false });

    // Apply pagination
    if (filters.limit) {
      query = query.limit(filters.limit);
    }
    if (filters.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching applications by job:', error);
      return { applications: [], total: 0 };
    }

    return {
      applications: (data as Application[]).map(app => ({
        ...app,
        submittedAt: new Date(app.submittedAt),
      })),
      total: count || 0,
    };
  } catch (error) {
    console.error('Error in getApplicationsByJob:', error);
    return { applications: [], total: 0 };
  }
};

// Get applications for an entity
export const getApplicationsByEntity = async (
  entityId: string,
  filters: ApplicationFilters = {}
): Promise<{ applications: Application[]; total: number }> => {
  try {
    let query = supabase
      .from('applications')
      .select('*, jobs(*)', { count: 'exact' })
      .eq('entity_id', entityId)
      .is('deleted_at', null);

    // Apply status filter
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    // Apply job filter
    if (filters.jobId) {
      query = query.eq('job_id', filters.jobId);
    }

    // Apply sorting
    query = query.order('submitted_at', { ascending: false });

    // Apply pagination
    if (filters.limit) {
      query = query.limit(filters.limit);
    }
    if (filters.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching applications by entity:', error);
      return { applications: [], total: 0 };
    }

    return {
      applications: (data as Application[]).map(app => ({
        ...app,
        submittedAt: new Date(app.submittedAt),
      })),
      total: count || 0,
    };
  } catch (error) {
    console.error('Error in getApplicationsByEntity:', error);
    return { applications: [], total: 0 };
  }
};

// Get application by ID
export const getApplicationById = async (
  id: string
): Promise<Application | null> => {
  try {
    const { data, error } = await supabase
      .from('applications')
      .select('*, jobs(*)')
      .eq('id', id)
      .is('deleted_at', null)
      .single();

    if (error) {
      console.error('Error fetching application by ID:', error);
      return null;
    }

    return {
      ...data,
      submittedAt: new Date(data.submitted_at),
    } as Application;
  } catch (error) {
    console.error('Error in getApplicationById:', error);
    return null;
  }
};

// Update application status
export const updateApplicationStatus = async (
  id: string,
  status: Application['status'],
  reviewedBy?: string,
  notes?: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('applications')
      .update({
        status,
        ...(reviewedBy && { reviewed_by: reviewedBy, reviewed_at: new Date().toISOString() }),
        ...(notes && { notes }),
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating application status:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in updateApplicationStatus:', error);
    return false;
  }
};

// Update application with screening results
export const updateApplicationScreening = async (
  id: string,
  matchScore: number,
  screeningResults: any
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('applications')
      .update({
        match_score: matchScore,
        screening_results: screeningResults,
        status: 'reviewing',
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating application screening:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in updateApplicationScreening:', error);
    return false;
  }
};

// Delete application (soft delete)
export const deleteApplication = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('applications')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      console.error('Error deleting application:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteApplication:', error);
    return false;
  }
};

// Get application statistics for entity
export const getApplicationStats = async (
  entityId: string
): Promise<{
  total: number;
  pending: number;
  reviewing: number;
  shortlisted: number;
  rejected: number;
  hired: number;
}> => {
  try {
    const { data, error } = await supabase
      .from('applications')
      .select('status')
      .eq('entity_id', entityId)
      .is('deleted_at', null);

    if (error) {
      console.error('Error fetching application stats:', error);
      return {
        total: 0,
        pending: 0,
        reviewing: 0,
        shortlisted: 0,
        rejected: 0,
        hired: 0,
      };
    }

    const stats = data.reduce(
      (acc, app) => {
        acc.total++;
        acc[app.status as keyof typeof acc]++;
        return acc;
      },
      {
        total: 0,
        pending: 0,
        reviewing: 0,
        shortlisted: 0,
        rejected: 0,
        hired: 0,
      }
    );

    return stats;
  } catch (error) {
    console.error('Error in getApplicationStats:', error);
    return {
      total: 0,
      pending: 0,
      reviewing: 0,
      shortlisted: 0,
      rejected: 0,
      hired: 0,
    };
  }
};
